import { HOST_DOMAIN } from "../../utils/domain";
import { getAuthorizationHeader } from "@brandclub/common-ui";
import { AppConfig, BrandAppConfig } from "../../redux/types";
import {
  parseLegacyDtcThemeResponse,
  ServerThemeType,
} from "@brandclub/common-ui";
const DOMAIN = `${HOST_DOMAIN}` + "/api-rewards";

interface AppSetupTheme {
  colorOptions: string[];
  fontOptions: string[];
  theme: ServerThemeType;
  draftId: string | undefined;
}

type GetAppThemeDraftResponse =
  | {
      brandAppTheme: ServerThemeType;
      configId: string;
      draftId: string;
      status: "success";
    }
  | {
      status: "failed";
      errorMessage: string;
    }
  | {
      status: "notFound";
      errorMessage: string;
    };

type GetAppSetupThemeResponse =
  | (AppSetupTheme & {
      status: "success";
    })
  | {
      status: "failed";
      errorMessage: string;
    }
  | {
      status: "notFound";
      errorMessage: string;
    };

export const getAppTheme = async (
  signal?: AbortSignal
): Promise<ServerThemeType> => {
  const themeAppId = window.location.origin.split("https://")[1];
  const appId = HOST_DOMAIN.split("https://")[1];
  const res = await fetch(DOMAIN + "/brandapp/getDTCAppTheme", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(await getAuthorizationHeader()),
    },
    body: JSON.stringify({
      appId,
      themeAppId,
    }),
    signal,
  });
  if (!res.ok) {
    throw new Error("Error fetching app theme");
  }
  const data = await res.json();
  const parsedData = parseLegacyDtcThemeResponse(data);
  if (!parsedData) {
    throw new Error("Error parsing app theme");
  }
  return parsedData;
};

export const getAppThemeDraft = async (
  appId: string,
  draftId: string,
  signal?: AbortSignal
): Promise<GetAppThemeDraftResponse> => {
  const res = await fetch(DOMAIN + "/brandapp/getAppThemeDraft", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(await getAuthorizationHeader()),
    },
    body: JSON.stringify({
      appId,
      draftId,
    }),
    signal,
  });
  if (res.ok) {
    return res.json();
  } else {
    console.error(res.statusText);
    throw new Error("Error fetching app theme draft");
  }
};

export const getAppSetupTheme = async (
  appId: string,
  setupId: string,
  signal?: AbortSignal
): Promise<GetAppSetupThemeResponse> => {
  const url = new URL(DOMAIN + "/brandapp/themeSetup");
  url.searchParams.set("appId", appId);
  url.searchParams.set("setupId", setupId);
  const res = await fetch(url.toString(), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...(await getAuthorizationHeader()),
    },
    signal,
  });
  if (res.ok) {
    return res.json();
  } else {
    console.error(res.statusText);
    throw new Error("Error fetching theme setup");
  }
};

export const getAppConfig = async (
  signal?: AbortSignal
): Promise<AppConfig> => {
  const res = await fetch(DOMAIN + "/config/webApp/getAppConfig", {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...(await getAuthorizationHeader()),
    },
    signal,
  });
  if (res.ok) {
    return res.json();
  } else {
    throw new Error("Error fetching app config");
  }
};

export const getBrandAppConfigs = async (
  signal?: AbortSignal
): Promise<BrandAppConfig> => {
  const res = await fetch(DOMAIN + "/brandapp/brandAppConfig", {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...(await getAuthorizationHeader()),
    },
    signal,
  });
  if (res.ok) {
    return res.json();
  } else {
    throw new Error("Error fetching brand app configs");
  }
};

/**
 * @deprecated use getBrandAppConfigs instead
 */
export const getBrandAppConfigsLegacyDoNotUse = async (
  appId: string,
  signal?: AbortSignal
): Promise<BrandAppConfig> => {
  const res = await fetch(DOMAIN + "/brandapp/getBrandAppConfig", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(await getAuthorizationHeader()),
    },
    body: JSON.stringify({
      appId,
    }),
    signal,
  });
  if (res.ok) {
    return res.json();
  } else {
    throw new Error("Error fetching brand app configs");
  }
};

export const redeemBrandAppLoginCode = async (
  req: {
    loginCode: string;
  },
  signal?: AbortSignal
) => {
  const res = await fetch(DOMAIN + "/brandapp/redeemBrandAppLoginCode", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(req),
    signal,
  });
  if (res.ok) {
    return res.json();
  } else {
    throw new Error("Error redeeming brand app login code");
  }
};
